import React, { useEffect } from 'react'
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
	Outlet,
	useLocation
} from 'react-router-dom'

import Admin from './admin/Admin'
import Users from './admin/Users'
import Products from './admin/Products'
import Product from './admin/Product'
import ThemePaletteBrowser from './admin/ThemePaletteBrowser'
import { useUserStore } from '../state'
import { logger } from '../util/log-utils'
import useNavBarHeight from '../hooks/useNavBarHeight'
import CheckAccess from './CheckAccess'
import ProductGenerator from './admin/ProductGenerator'
import TopicJobs from './admin/TopicJobs'
import AppConfigManager from './admin/AppConfigManager'
import PromptEditor     from './admin/PromptEditor'
import ProductQuestions from './admin/ProductQuestions'
import Videos from './admin/Videos'
import VideoJobProcessing from './admin/VideoJobProcessing'
import QuestionView from './app/QuestionView'
import AppLayout from './layout/AppLayout'
import CodeView from './app/CodeView'
import AuthIntro from './auth/AuthIntro'
import Packages from './admin/Packages'
import Package from './admin/Package'
import useUserRoles from '../hooks/useUserRoles'
import BadJson from './BadJson'
import PrepKit from './app/PrepKit'
import PrepKits from './app/PrepKits'
import Shop from './app/Shop'
import PrepKitDashboard from './app/PrepKitDashboard'
import ProgressReport       from './app/ProgressReport'
import Categories from './admin/Categories'
import UserProfile from './profile/UserProfile'
import AuthCallback from './auth/AuthCallback'
import AiPersonalityManager from './admin/AiPersonaltyManager'
import RouteLogger from './RouteLogger'
import Analytics from './Analytics'
import AdminLayout from './layout/AdminLayout'
import PackageDetail from './app/PackageDetail'
import Category from './app/Category'
import RenewPrepKit from './app/RenewPrepKit'
import Conversions from './admin/Conversions'
import CouponRedirect from './app/CouponRedirect'
import ActiveMediaJobs from './admin/ActiveMediaJobs'
import ManageUser from './admin/ManageUser'
import TryPackageScreen from './app/TryPackageScreen'
import BuyPackageScreen from './app/BuyPackageScreen'
import AnonQuestionView from './app/AnonQuestionView'
import ProductTts from './admin/ProductTts'
import ProductMediaCache from './admin/ProductMediaCache'
import ImportPrepKit from './admin/ImportPrepKit'
import PromptTemplates from './admin/PromptTemplates'
import WelcomeMessages from './admin/WelcomeMessages'
import Announcements from './admin/Announcements'
import AnnouncementGate from './app/AnnouncementGate'


const log = logger('AuthGate', 1)
const routeLoggingEnabled = false

/* =====================
   AUTH GUARDS (INLINE)
   ===================== */

const RequireAuth = () => {
	const { user, authLoaded } = useUserStore()
	const location = useLocation()

	if (!authLoaded) return null

	const isAuthPage =
		location.pathname === '/login' ||
		location.pathname.startsWith('/auth')

	// 🔑 CRITICAL: auth pages are never guarded
	if (isAuthPage) {
		return <Outlet />
	}

	if (!user) {
		const intended = encodeURIComponent(
			`${location.pathname}${location.search}${location.hash || ''}`
		)
		return (
			<Navigate
				to={`/login?next=${intended}`}
				replace
			/>
		)
	}

	return <Outlet />
}

const RequireAdmin = () => {
	const { authLoaded } = useUserStore()
	const isAdmin = useUserRoles(['admin'])

	const ur = useUserRoles()
	console.log(JSON.stringify({ur}))

	if (!authLoaded) return null
	if (!isAdmin) return <Navigate to="/" replace />

	return <Outlet />
}

/* =====================
   AUTH GATE
   ===================== */

function AuthGate({ toggleTheme }) {
	log.trace('Component render')

	const { user } = useUserStore()
	const isAdmin = useUserRoles(['admin'])
	const { navBarRef, height } = useNavBarHeight()

	useEffect(() => {
		if (navBarRef.current) {
			document.documentElement.style.setProperty(
				'--navbar-height',
				`${height}px`
			)
		}
	}, [height, navBarRef])

	return (
		<Router>
			<Analytics user={user} isAdmin={isAdmin} />
			<RouteLogger enabled={routeLoggingEnabled} />
			{/*
			  * The in-app announcement (190). Mounted once for the whole session here rather than
			  * per screen -- see AnnouncementGate for why this is the right place.
			  */}
			<AnnouncementGate />

			<Routes>

				{/* ===== ORIGINAL SHARED ROUTES (ORDER PRESERVED) ===== */}

				{/*
				  * "/" goes straight to the prepkit list. RequireAuth still guards /prepkit, so a
				  * signed-out visitor lands on /login?next=/prepkit and arrives where they were
				  * headed once signed in.
				  */}
				<Route path="/" element={<AppLayout toggleTheme={toggleTheme} />}>
					<Route index element={<Navigate to="/prepkit" replace />} />
				</Route>

				<Route path="/auth/callback" element={<AuthCallback />} />

				<Route path="/q" element={<AppLayout toggleTheme={toggleTheme} />}>
					<Route index element={<CodeView />} />
					<Route path=":code" element={<AnonQuestionView />} />
				</Route>

				<Route path="/prep" element={<AppLayout toggleTheme={toggleTheme} />}>
					<Route index element={<Shop />} />
					<Route path=":categoryToken" element={<Category />} />
					<Route path=":categoryToken/:packageToken" element={<PackageDetail />} />
				</Route>

				<Route path="/try" element={<AppLayout toggleTheme={toggleTheme} />}>
					<Route path=":categoryToken/:packageToken" element={<TryPackageScreen />} />
				</Route>

				<Route path="/buy" element={<AppLayout toggleTheme={toggleTheme} />}>
					<Route
						path=":categoryToken/:packageToken/:optionId"
						element={<BuyPackageScreen />}
					/>
				</Route>

				{/* ===== AUTHENTICATED ROUTES (WERE authenticatedRoutes) ===== */}

				<Route element={<RequireAuth />}>
					<Route element={<AppLayout toggleTheme={toggleTheme} />}>
						<Route path="/renew/:id" element={<RenewPrepKit />} />
						<Route path="/coupon" element={<CouponRedirect />} />
					</Route>

					{/* admin keeps a full-width, non-centred wrapper */}
					<Route element={<AppLayout plain toggleTheme={toggleTheme} />}>
						<Route element={<RequireAdmin />}>
							<Route path="/admin" element={<AdminLayout />}>
								<Route index element={<Admin />} />
								<Route path="conversions" element={<Conversions />} />
								<Route path="users" element={<Users />} />
								<Route path="users/:id" element={<ManageUser />} />
								<Route path="products" element={<Products />} />
								<Route path="products/create" element={<ProductGenerator />} />
								<Route path="products/:productId" element={<Product />}>
									<Route index element={<ProductQuestions />} />
									<Route path="tts" element={<ProductTts />} />
									<Route path="media-cache" element={<ProductMediaCache />} />
								</Route>
								<Route path="packages/new" element={<Package />} />
								<Route path="packages/:id/edit" element={<Package />} />
								<Route path="packages" element={<Packages />} />
								<Route path="topic-job-queue" element={<TopicJobs />} />
								<Route path="videos" element={<Videos />}>
									<Route path="create" element={null} />
								</Route>
								<Route path="videos/jobs/:id" element={<VideoJobProcessing />} />
								<Route path="categories" element={<Categories />} />
								<Route path="active-media-jobs" element={<ActiveMediaJobs />} />
								<Route path="app-config" element={<AppConfigManager />} />
								<Route path="prompts" element={<PromptEditor />} />
								<Route path="ai-personalities" element={<AiPersonalityManager />} />
								<Route path="palette" element={<ThemePaletteBrowser />} />
								<Route path="check-access" element={<CheckAccess />} />
								<Route path="bad-json" element={<BadJson />} />
								<Route path="import-prepkit" element={<ImportPrepKit />} />
								<Route path="prompt-templates" element={<PromptTemplates />} />
								<Route path="welcome-messages" element={<WelcomeMessages />} />
								<Route path="announcements" element={<Announcements />} />
							</Route>
						</Route>

					</Route>

					{/* ===== MINI / ACCOUNT (ORDER PRESERVED) ===== */}

					<Route path="/prepkit" element={<AppLayout toggleTheme={toggleTheme} />}>
						<Route index element={<PrepKits />} />
						<Route path=":id" element={<PrepKit />}>
							<Route index element={<PrepKitDashboard />} />
							<Route path="report" element={<ProgressReport />} />
							<Route path="t/:topicId" element={<PrepKitDashboard />} />
							<Route path="t/:topicId/q/:code" element={<QuestionView />} />
							<Route path="q/:code" element={<QuestionView />} />
						</Route>
					</Route>

					<Route path="/account" element={<AppLayout toggleTheme={toggleTheme} />}>
						<Route path="profile" element={<UserProfile />} />
					</Route>

				</Route>

				{/* ===== AUTH UI ===== */}

				<Route path="/login" element={<AuthIntro />} />

				{/* ===== FALLBACK ===== */}
				<Route path="*" element={<Navigate to="/" replace />} />

			</Routes>
		</Router>
	)
}

export default React.memo(AuthGate)
